import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { emailAccountsAPI } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { EmailAccount } from '@/types/email-accounts';
import { 
  Mail, 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  ExternalLink,
  Unlink,
  RefreshCw
} from 'lucide-react';

interface GmailOAuthProps {
  onConnectionChange?: (isConnected: boolean) => void;
}

export function GmailOAuth({ onConnectionChange }: GmailOAuthProps) {
  const [isConnecting, setIsConnecting] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Check for OAuth callback parameters in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      toast({
        title: 'Authentication Failed',
        description: `Failed to connect Gmail: ${error}`,
        variant: 'destructive',
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code) {
      handleOAuthCallback(code, state);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const { data: accountsResponse, isLoading, error } = useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => emailAccountsAPI.getAccounts(),
  });

  const accounts = accountsResponse?.data as EmailAccount[] | undefined || [];
  const gmailAccount = accounts.find(account => account.provider === 'gmail');
  const isConnected = !!gmailAccount && gmailAccount.isActive;

  const connectGmailMutation = useMutation({
    mutationFn: () => emailAccountsAPI.getGmailAuthUrl(),
    onSuccess: (response: any) => {
      const authUrl = response.data.authUrl;
      // Redirect to Gmail OAuth
      window.location.href = authUrl;
    },
    onError: (error: any) => {
      setIsConnecting(false);
      toast({
        title: 'Connection Failed',
        description: error.response?.data?.message || 'Failed to initiate Gmail connection',
        variant: 'destructive',
      });
    },
  });

  const handleOAuthCallbackMutation = useMutation({
    mutationFn: (data: { code: string; state?: string }) => 
      emailAccountsAPI.handleGmailCallback(data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
      toast({
        title: 'Gmail Connected',
        description: `Successfully connected Gmail account: ${response.data.email}`,
      });
      onConnectionChange?.(true);
      setIsConnecting(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Connection Failed',
        description: error.response?.data?.message || 'Failed to connect Gmail account',
        variant: 'destructive',
      });
      setIsConnecting(false);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => emailAccountsAPI.disconnectAccount('gmail'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
      toast({
        title: 'Gmail Disconnected',
        description: 'Gmail account has been disconnected from your team.',
      });
      onConnectionChange?.(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Disconnection Failed',
        description: error.response?.data?.message || 'Failed to disconnect Gmail account',
        variant: 'destructive',
      });
    },
  });

  const handleConnect = () => {
    setIsConnecting(true);
    connectGmailMutation.mutate();
  };

  const handleDisconnect = () => {
    if (confirm('Are you sure you want to disconnect your Gmail account? This will stop all email sending capabilities.')) {
      disconnectMutation.mutate();
    }
  };

  const handleOAuthCallback = (code: string, state: string | null) => {
    setIsConnecting(true);
    const callbackData: { code: string; state?: string } = { code };
    if (state) callbackData.state = state;
    handleOAuthCallbackMutation.mutate(callbackData);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <LoadingSpinner size="lg" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load email accounts. Please try again.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Mail className="h-5 w-5" />
          <span>Gmail Integration</span>
          {isConnected && (
            <Badge variant="default" className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isConnected && gmailAccount ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                  <Mail className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-900">{gmailAccount.email}</p>
                  <p className="text-sm text-green-700">
                    Connected on {new Date(gmailAccount.connectedAt).toLocaleDateString()}
                  </p>
                  {gmailAccount.lastUsedAt && (
                    <p className="text-xs text-green-600">
                      Last used: {new Date(gmailAccount.lastUsedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['email-accounts'] })}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnect}
                  disabled={disconnectMutation.isPending}
                >
                  <Unlink className="h-3 w-3 mr-1" />
                  Disconnect
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium flex items-center">
                <Shield className="h-4 w-4 mr-2 text-blue-600" />
                Permissions Granted
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• Send emails on your behalf</li>
                <li>• Compose and modify drafts</li>
                <li>• Access your email address</li>
              </ul>
            </div>

            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Your Gmail account is successfully connected and ready to send campaigns.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center py-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                <Mail className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium mb-2">Connect Your Gmail Account</h3>
              <p className="text-muted-foreground mb-6">
                Connect your Gmail account to send email campaigns and manage outreach from LeadGPT.
              </p>
              
              <Button
                onClick={handleConnect}
                disabled={isConnecting || connectGmailMutation.isPending}
                className="w-full sm:w-auto"
              >
                {isConnecting || connectGmailMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Connect Gmail Account
                  </>
                )}
              </Button>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium flex items-center">
                <Shield className="h-4 w-4 mr-2 text-blue-600" />
                What we'll access
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• Send emails on your behalf</li>
                <li>• Compose and modify email drafts</li>
                <li>• Access your email address for identification</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                We only access what's necessary for email campaigns and never read your personal emails.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}